import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  useGetTotalUsersQuery,
  useGetQuotationsQuery,
  useGetEstimatesQuery,
  useGetProposalsQuery,
  useGetContractsQuery,
  useGetProposalStatisticsQuery,
  useGetEstimateDashboardQuery,
  useGetProjectsDashboardQuery,
  useGetDashboardQuotationsQuery,
  useGetContractsDashboardQuery,
} from "../Redux/Service/DashboardApi";
import StyledDashboardLayout from "./style";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

function Dashboard() {
  const chartRef = useRef(null);
  const lineChartRef = useRef(null);
  const dountChartRef = useRef(null);
  const barChartRef = useRef(null);
  const areaChartRef = useRef(null);

  // Projects state
  const [projDate] = useState(new Date());
  const [projViewType, setProjViewType] = useState("specificYear");
  const [projFromYear, setProjFromYear] = useState(projDate.getFullYear() - 5);
  const [projToYear, setProjToYear] = useState(projDate.getFullYear());
  const [projYearInput, setProjYearInput] = useState(projDate.getFullYear());
  const [tempProjFromYear, setTempProjFromYear] = useState(projFromYear);
  const [tempProjToYear, setTempProjToYear] = useState(projToYear);
  const [projModalOpen, setProjModalOpen] = useState(false);

  // Estimates state
  const [estDate] = useState(new Date());
  const [estViewType, setEstViewType] = useState("specificYear");
  const [estFromYear, setEstFromYear] = useState(estDate.getFullYear() - 5);
  const [estToYear, setEstToYear] = useState(estDate.getFullYear());
  const [estYearInput, setEstYearInput] = useState(estDate.getFullYear());
  const [tempEstFromYear, setTempEstFromYear] = useState(estFromYear);
  const [tempEstToYear, setTempEstToYear] = useState(estToYear);
  const [estModalOpen, setEstModalOpen] = useState(false);

  // Proposals state
  const [propDate] = useState(new Date());
  const [propViewType, setPropViewType] = useState("specificYear");
  const [propFromYear, setPropFromYear] = useState(propDate.getFullYear() - 5);
  const [propToYear, setPropToYear] = useState(propDate.getFullYear());
  const [propYearInput, setPropYearInput] = useState(propDate.getFullYear());
  const [tempPropFromYear, setTempPropFromYear] = useState(propFromYear);
  const [tempPropToYear, setTempPropToYear] = useState(propToYear);
  const [propModalOpen, setPropModalOpen] = useState(false);

  // Quotations state
  const [quotationDate] = useState(new Date());
  const [quotationViewType, setQuotationViewType] = useState("specificYear");
  const [quotationFromYear, setQuotationFromYear] = useState(
    quotationDate.getFullYear() - 5
  );
  const [quotationToYear, setQuotationToYear] = useState(
    quotationDate.getFullYear()
  );
  const [quotationYearInput, setQuotationYearInput] = useState(
    quotationDate.getFullYear()
  );
  const [tempQuotationFromYear, setTempQuotationFromYear] =
    useState(quotationFromYear);
  const [tempQuotationToYear, setTempQuotationToYear] =
    useState(quotationToYear);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);

  // Contracts state
  const [contractDate] = useState(new Date());
  const [propContractFromYear, setPropContractFromYear] = useState(
    contractDate.getFullYear() - 5
  );
  const [propContractToYear, setPropContractToYear] = useState(
    contractDate.getFullYear()
  );
  const [propContractYearInput, setPropContractYearInput] = useState(
    contractDate.getFullYear()
  );
  const [tempContractFromYear, setTempContractFromYear] =
    useState(propContractFromYear);
  const [tempContractToYear, setTempContractToYear] =
    useState(propContractToYear);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractType] = useState("specificYear");
  const [propContractViewType, setPropContractViewType] =
    useState("specificYear"); // "specificYear" or "range"

  // fetch dashboards with their own params
  const {
    data: projectsData,
    isLoading: projectsLoading,
    isError: projectsError,
    refetch: refetchProjects,
  } = useGetProjectsDashboardQuery({
    viewType: projViewType,
    yearInput: projYearInput,
    fromYear: projFromYear,
    toYear: projToYear,
  });

  const {
    data: estimateStats,
    isLoading: estimatesLoading,
    isError: estimatesError,
    refetch: refetchEstimates,
  } = useGetEstimateDashboardQuery({
    viewType: estViewType,
    yearInput: estYearInput,
    fromYear: estFromYear,
    toYear: estToYear,
  });

  const {
    data: dashboardQuotations,
    isLoading: quotationsLoading,
    isError: quotationsError,
    refetch: refetchDashboardQuotations,
  } = useGetDashboardQuotationsQuery({
    viewType: quotationViewType,
    yearInput: quotationYearInput,
    fromYear: quotationFromYear,
    toYear: quotationToYear,
  });

  const {
    data: contractData,
    isLoading: contractsLoading,
    isError: contractsError,
    refetch: refetchContracts,
  } = useGetContractsDashboardQuery({
    viewType: propContractViewType, // "specificYear" or "range"
    yearInput: propContractYearInput,
    fromYear: propContractFromYear,
    toYear: propContractToYear,
  });

  // build dynamic params for proposals
  const proposalParams =
    propViewType === "specificYear"
      ? { viewType: propViewType, yearInput: propYearInput } // Only send yearInput for specificYear
      : { viewType: propViewType, fromYear: propFromYear, toYear: propToYear }; // Only send range for yearRange

  const {
    data: proposalStats,
    isLoading: proposalsLoading,
    isError: proposalsError,
    refetch: refetchProposals,
  } = useGetProposalStatisticsQuery(proposalParams);

  // Modal handlers
  const handleProjModalOpen = () => {
    setTempProjFromYear(projFromYear);
    setTempProjToYear(projToYear);
    setProjModalOpen(true);
  };
  const handleProjModalClose = () => {
    setTempProjFromYear(projFromYear);
    setTempProjToYear(projToYear);
    setProjModalOpen(false);
  };
  const handleProjSubmitYearRange = () => {
    if (tempProjToYear >= tempProjFromYear) {
      setProjFromYear(tempProjFromYear);
      setProjToYear(tempProjToYear);
      setProjYearInput(
        projYearInput >= tempProjFromYear && projYearInput <= tempProjToYear
          ? projYearInput
          : tempProjToYear
      );
      setProjViewType("range");
      handleProjModalClose();
    }
  };

  const handleEstModalOpen = () => {
    setTempEstFromYear(estFromYear);
    setTempEstToYear(estToYear);
    setEstModalOpen(true);
  };
  const handleEstModalClose = () => {
    setTempEstFromYear(estFromYear);
    setTempEstToYear(estToYear);
    setEstModalOpen(false);
  };

  const handleEstSubmitYearRange = () => {
    if (tempEstToYear >= tempEstFromYear) {
      setEstFromYear(tempEstFromYear);
      setEstToYear(tempEstToYear);
      setEstViewType("range");
      handleEstModalClose();
    }
  };

  const handlePropModalOpen = () => {
    setTempPropFromYear(propFromYear);
    setTempPropToYear(propToYear);
    setPropModalOpen(true);
  };
  const handlePropModalClose = () => {
    setTempPropFromYear(propFromYear);
    setTempPropToYear(propToYear);
    setPropModalOpen(false);
  };
  const handlePropSubmitYearRange = () => {
    if (tempPropToYear >= tempPropFromYear) {
      setPropFromYear(tempPropFromYear);
      setPropToYear(tempPropToYear);
      setPropYearInput(
        Math.max(tempPropFromYear, Math.min(tempPropToYear, propYearInput))
      );
      handlePropModalClose();
    }
  };

  const handleQuotationModalOpen = () => {
    setTempQuotationFromYear(quotationFromYear);
    setTempQuotationToYear(quotationToYear);
    setQuotationModalOpen(true);
  };
  const handleQuotationModalClose = () => {
    setTempQuotationFromYear(quotationFromYear);
    setTempQuotationToYear(quotationToYear);
    setQuotationModalOpen(false);
  };

  const handleQuotationSubmitYearRange = () => {
    if (tempQuotationToYear >= tempQuotationFromYear) {
      setQuotationFromYear(tempQuotationFromYear);
      setQuotationToYear(tempQuotationToYear);
      setQuotationYearInput(tempQuotationFromYear);
      setQuotationViewType("range"); // 🔁 switch to range view
      handleQuotationModalClose();
    }
  };

  const handleContractModalOpen = () => {
    setTempContractFromYear(propContractFromYear);
    setTempContractToYear(propContractToYear);
    setContractModalOpen(true);
  };
  const handleContractModalClose = () => {
    setTempContractFromYear(propContractFromYear);
    setTempContractToYear(propContractToYear);
    setContractModalOpen(false);
  };

  const handleContractSubmitYearRange = () => {
    setPropContractFromYear(tempContractFromYear);
    setPropContractToYear(tempContractToYear);
    setPropContractViewType("range");
    handleContractModalClose();
  };

  // new dynamic donut reading from estimateStats
  useEffect(() => {
    if (!dountChartRef.current || estimatesLoading || estimatesError) return;

    console.log("RENDERING CHART:", {
      viewType: estViewType,
      year: estYearInput,
      range: [estFromYear, estToYear],
      data: estimateStats?.response,
    });

    let rawData = estimateStats?.response || [];
    // For specificYear, ensure empty array yields no-data
    if (estViewType === "specificYear" && rawData.length === 0) {
      rawData = []; // So fallback kicks in
    }

    const hasData =
      rawData.length > 0 &&
      rawData.some((item) => parseInt(item.count, 10) > 0);

    const fallback = [{ tech: "No Data", count: 0 }];
    const dataToUse = hasData ? rawData : fallback;

    const series = dataToUse.map((i) => parseInt(i.count, 10));
    const labels = dataToUse.map((i) => i.tech);

    // Clean up existing chart before rendering
    if (dountChartRef.current.__chart__) {
      dountChartRef.current.__chart__.destroy();
      if (dountChartRef.current) dountChartRef.current.innerHTML = "";
    }

    const chart = new window.ApexCharts(dountChartRef.current, {
      chart: { type: "donut", height: 320 },
      series,
      labels,
      colors: hasData
        ? ["#1C41FF", "#EB696E", "#81F189", "#F8C15C", "#B388EB", "#FFB6C1", "#7184FD"]
        : ["#E0E0E0"],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        markers: {
          width: 12,
          height: 12,
          radius: 12,
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "75%",
            labels: {
              show: true,
              total: {
                show: true,
                label: hasData ? "Total" : "No Data",
                formatter: () =>
                  hasData ? `${series.reduce((a, b) => a + b, 0)}` : "0",
              },
            },
          },
        },
      },
    });

    chart.render();
    dountChartRef.current.__chart__ = chart;

    return () => {
      // Fix: Only access innerHTML if ref is still mounted
      if (dountChartRef.current) {
        if (dountChartRef.current.__chart__) {
          dountChartRef.current.__chart__.destroy();
          delete dountChartRef.current.__chart__;
        }
        dountChartRef.current.innerHTML = "";
      }
    };
  }, [estimateStats, estimatesLoading, estimatesError, estViewType]);

  useEffect(() => {
    if (quotationsLoading || quotationsError || !barChartRef.current) return;

    const items = dashboardQuotations?.response || [];
    const isYearRange = quotationViewType === "range";

    let categories = [];
    let seriesData = [];

    if (isYearRange) {
      // Handle year range response
      const sortedYears = items
        .filter((item) => typeof item.year !== "undefined")
        .sort((a, b) => a.year - b.year);

      categories = sortedYears.map((item) => item.year.toString());
      seriesData = sortedYears.map((item) => parseInt(item.count, 10) || 0);
    } else {
      // Handle specific year response (monthly data)
      const monthly = Array(12).fill(0);
      items.forEach(({ month, count }) => {
        const index = (month ?? 0) - 1;
        if (index >= 0 && index < 12) {
          monthly[index] = parseInt(count, 10) || 0;
        }
      });
      categories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      seriesData = monthly;
    }

    const options = {
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      series: [{ name: "Quotations", data: seriesData }],
      colors: ["#4285f4"],
      plotOptions: {
        bar: { borderRadius: 4, columnWidth: "75%", endingShape: "rounded" },
      },
      xaxis: {
        categories,
        labels: { style: { fontSize: "16px", colors: "#888" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      grid: { show: false },
      dataLabels: { enabled: false },
      tooltip: { enabled: true },
      legend: { show: false },
      fill: { opacity: 1 },
    };

    const chart = new window.ApexCharts(barChartRef.current, options);
    chart.render();

    return () => {
      if (barChartRef.current) {
        chart.destroy();
        barChartRef.current.innerHTML = "";
      }
    };
  }, [
    dashboardQuotations,
    quotationsLoading,
    quotationsError,
    quotationViewType,
  ]);

  const [period, setPeriod] = useState("Monthly");

  // Dynamically omit yearInput if in "yearRange" mode
  const apiParams =
    propViewType === "specificYear"
      ? { from: propFromYear, to: propToYear, yearInput: propYearInput }
      : { from: propFromYear, to: propToYear };

  // const { data, isLoading, isError } = useGetContractDashboardQuery(apiParams);

  const handleChange = (event) => setPeriod(event.target.value);
  const handleViewChange = (event) => setProjViewType(event.target.value);

  useEffect(() => {
    if (contractsLoading || contractsError || !areaChartRef.current) return;

    const items = contractData?.response || [];

    let categories = [];
    let seriesData = [];

    if (propContractViewType === "specificYear") {
      // Month-wise view
      categories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthly = Array(12).fill(0);

      items.forEach(({ month, count }) => {
        if (month) {
          monthly[parseInt(month, 10) - 1] = parseInt(count, 10);
        }
      });

      seriesData = monthly;
    } else if (propContractViewType === "range") {
      // Year-wise view
      for (
        let year = propContractFromYear;
        year <= propContractToYear;
        year++
      ) {
        const found = items.find((i) => parseInt(i.year, 10) === year);
        categories.push(String(year));
        seriesData.push(found ? parseInt(found.count, 10) : 0);
      }
    }

    const options = {
      chart: {
        type: "area",
        height: 300,
        toolbar: { show: false },
      },
      series: [{ name: "Contracts", data: seriesData }],
      xaxis: {
        categories,
        labels: {
          style: {
            colors: "#999",
            fontSize: "14px",
          },
        },
      },
      stroke: { curve: "smooth", width: 2, colors: ["#3F30FF"] },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.8,
          gradientToColors: ["#ffffff"],
          opacityFrom: 0.6,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
      tooltip: { shared: false, intersect: false },
      dataLabels: { enabled: false },
      legend: { show: false },
    };

    const chart = new window.ApexCharts(areaChartRef.current, options);
    chart.render();

    return () => {
      if (areaChartRef.current) {
        chart.destroy();
        areaChartRef.current.innerHTML = "";
      }
    };
  }, [
    contractData,
    contractsLoading,
    contractsError,
    propContractViewType,
    propContractFromYear,
    propContractToYear,
    propContractYearInput,
  ]);

  useEffect(() => {
    if (
      proposalsLoading ||
      proposalsError ||
      !proposalStats ||
      !lineChartRef.current
    )
      return;

    const items = proposalStats.response?.data || proposalStats.response || [];
    let categories = [];
    let seriesData = [];

    if (propViewType === "specificYear") {
      categories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthly = Array(12).fill(0);
      items.forEach(({ month, count }) => {
        if (month && count !== undefined)
          monthly[month - 1] = parseInt(count, 10);
      });
      seriesData = monthly;
    } else {
      for (let y = propFromYear; y <= propToYear; y++) {
        categories.push(String(y));
        const found = items.find((i) => parseInt(i.year, 10) === y);
        seriesData.push(found ? parseInt(found.count, 10) : 0);
      }
    }

    const options = {
      chart: { type: "line", height: 300, toolbar: { show: false } },
      series: [{ name: "Proposals", data: seriesData }],
      stroke: { curve: "smooth", width: 2, colors: ["#3F30FF"] },
      xaxis: {
        categories,
        labels: { style: { colors: "#888", fontSize: "16px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      tooltip: { enabled: true, theme: "light" },
      grid: { show: true, borderColor: "#eee", strokeDashArray: 4 },
      dataLabels: { enabled: false },
      markers: {
        size: 5,
        colors: ["#fff"],
        strokeColors: "#3F30FF",
        strokeWidth: 2,
      },
      legend: { show: false },
    };

    const chart = new window.ApexCharts(lineChartRef.current, options);
    chart.render();
    return () => {
      if (lineChartRef.current) {
        chart.destroy();
        lineChartRef.current.innerHTML = "";
      }
    };
  }, [
    proposalStats,
    proposalsLoading,
    proposalsError,
    propViewType,
    propFromYear,
    propToYear,
    propYearInput,
  ]);

  useEffect(() => {
    if (projectsLoading || projectsError || !chartRef.current) return;

    const lost = projectsData?.response?.dataLost ?? [];
    const won = projectsData?.response?.dataWon ?? [];

    const isYearRange = projViewType === "range";

    let categories = [];
    let seriesWon = [];
    let seriesLost = [];

    if (isYearRange) {
      const years = Array.from(
        { length: projToYear - projFromYear + 1 },
        (_, i) => projFromYear + i
      );
      categories = years.map((y) => y.toString());

      seriesWon = years.map((year) => {
        const entry = won.find((item) => item.year === year);
        return entry ? parseInt(entry.count, 10) : 0;
      });

      seriesLost = years.map((year) => {
        const entry = lost.find((item) => item.year === year);
        return entry ? parseInt(entry.count, 10) : 0;
      });
    } else {
      categories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      seriesWon = Array(12).fill(0);
      seriesLost = Array(12).fill(0);

      won.forEach(({ month, count }) => {
        if (month && count !== undefined)
          seriesWon[month - 1] = parseInt(count, 10);
      });
      lost.forEach(({ month, count }) => {
        if (month && count !== undefined)
          seriesLost[month - 1] = parseInt(count, 10);
      });
    }

    const options = {
      chart: { type: "area", height: 320, toolbar: { show: false } },
      colors: ["#28B316", "#3F30FF"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories,
        title: { text: isYearRange ? "Year" : "Month" },
      },
      legend: {
        show: true,
        position: "bottom",
        markers: { width: 10, height: 10, radius: 2 },
      },
      tooltip: { shared: true, intersect: false },
      series: [
        { name: "Project Won", data: seriesWon },
        { name: "Project Loss", data: seriesLost },
      ],
    };

    const chart = new window.ApexCharts(chartRef.current, options);
    chart.render();

    return () => {
      if (chartRef.current) {
        chart.destroy();
        chartRef.current.innerHTML = "";
      }
    };
  }, [
    projectsData,
    projectsLoading,
    projectsError,
    projViewType,
    projFromYear,
    projToYear,
  ]);

  const { data: totalOpportunities } = useGetTotalUsersQuery();
  const { data: estimates } = useGetEstimatesQuery();
  const { data: quotations } = useGetQuotationsQuery();
  const { data: proposals } = useGetProposalsQuery();
  const { data: contracts } = useGetContractsQuery();

  const cards = [
    {
      label: "Opportunities",
      value: totalOpportunities,
      color: "#ffeeee",
      icon: <PersonIcon />,
      iconBg: "#f28b82",
    },
    {
      label: "Estimates",
      value: estimates,
      color: "#fff9db",
      icon: <ReceiptIcon />,
      iconBg: "#fbbc04",
    },
    {
      label: "Quotations",
      value: quotations,
      color: "#eaffea",
      icon: <RequestQuoteIcon />,
      iconBg: "#34a853",
    },
    {
      label: "Proposals",
      value: proposals,
      color: "#e7f0fd",
      icon: <DescriptionIcon />,
      iconBg: "#4285f4",
    },
    {
      label: "Contract",
      value: contracts,
      color: "#f3e8fd",
      icon: <GavelIcon />,
      iconBg: "#a142f4",
    },
  ];
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // refetch on each section’s params
  useEffect(() => {
    refetchProjects();
    refetchEstimates();
    refetchProposals();
    refetchDashboardQuotations();
    refetchContracts();
  }, [
    projYearInput,
    projViewType,
    projFromYear,
    projToYear,
    estYearInput,
    estViewType,
    estFromYear,
    estToYear,
    propYearInput,
    propViewType,
    propFromYear,
    propToYear,
    quotationYearInput,
    quotationViewType,
    quotationFromYear,
    quotationToYear,
    propContractYearInput,
    contractType,
    propContractFromYear,
    propContractToYear,
  ]);

  return (
    <StyledDashboardLayout>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Dashboard</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="#">Home</Link>
                  </li>
                  <li className="breadcrumb-item active">Dashboard</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <Grid container spacing={2} sx={{ padding: "20px" }}>
          {cards.map((card, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  backgroundColor: card.color,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  borderRadius: 2,
                  height: "100%",
                }}
                className="cardBox"
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <Avatar sx={{ bgcolor: card.iconBg, width: 30, height: 30 }}>
                    {card.icon}
                  </Avatar>
                  <Typography variant="h6" color="textSecondary">
                    {card.label}
                  </Typography>
                </Box>
                <Typography variant="h3">{card.value}</Typography>
              </Paper>
            </Grid>
          ))}

          {/* Projects Section */}
          <Grid item md={7} xs={12}>
            <Paper className="graphBox">
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Projects</Typography>
                <Box display="flex" gap={2}>
                  <Button onClick={handleProjModalOpen} className="modalBtn">
                    Year Range
                  </Button>
                  <Modal
                    open={projModalOpen}
                    onClose={handleProjModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        p: 4,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "16px" }}>
                            <DatePicker
                              label="From Year"
                              views={["year"]}
                              value={
                                tempProjFromYear
                                  ? dayjs(`${tempProjFromYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempProjFromYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempProjFromYear || projFromYear}-01-01`
                              )}
                              maxDate={dayjs(`${projDate.getFullYear()}-12-31`)}
                              sx={{ minWidth: 120 }}
                            />
                            <DatePicker
                              label="To Year"
                              views={["year"]}
                              value={
                                tempProjToYear
                                  ? dayjs(`${tempProjToYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempProjToYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempProjFromYear || projFromYear}-01-01`
                              )}
                              maxDate={dayjs(`${projDate.getFullYear()}-12-31`)}
                              sx={{ minWidth: 120 }}
                            />
                          </div>
                          <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={handleProjModalClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleProjSubmitYearRange}
                              disabled={tempProjToYear < tempProjFromYear}
                            >
                              Submit
                            </Button>
                          </Box>
                        </div>
                      </LocalizationProvider>
                    </Box>
                  </Modal>
                  <FormControl
                    sx={{
                      minWidth: 90,
                      border: "1px solid #EDF0F3",
                      borderRadius: "7px",
                      px: 1,
                      py: 0.5,
                      backgroundColor: "#fff",
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#363434",
                    }}
                    variant="outlined"
                  >
                    <Select
                      value={projYearInput}
                      onChange={(e) => {
                        setProjViewType("specificYear"); // 👈 Switch back to specific year view
                        setProjYearInput(Number(e.target.value));
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        "& .MuiSelect-select": { py: 0.5 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      {Array.from(
                        { length: projToYear - projFromYear + 1 },
                        (_, i) => projFromYear + i
                      ).map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <div ref={chartRef} />
            </Paper>
          </Grid>

          {/* Estimates Section */}
          <Grid item md={5} xs={12}>
            <Paper className="graphBox">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Techstacks</Typography>
                <Box display="flex" gap={2}>
                  <Button onClick={handleEstModalOpen} className="modalBtn">
                    Year Range
                  </Button>
                  <Modal
                    open={estModalOpen}
                    onClose={handleEstModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        p: 4,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "16px" }}>
                            <DatePicker
                              label="From Year"
                              views={["year"]}
                              value={
                                tempEstFromYear
                                  ? dayjs(`${tempEstFromYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempEstFromYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempEstFromYear || estFromYear}-01-01`
                              )}
                              maxDate={dayjs(
                                `${tempEstToYear || estToYear}-12-31`
                              )}
                              sx={{ minWidth: 120 }}
                            />
                            <DatePicker
                              label="To Year"
                              views={["year"]}
                              value={
                                tempEstToYear
                                  ? dayjs(`${tempEstToYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue) setTempEstToYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempEstFromYear || estFromYear}-01-01`
                              )}
                              maxDate={dayjs(`${estDate.getFullYear()}-12-31`)}
                              sx={{ minWidth: 120 }}
                            />
                          </div>
                          <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={handleEstModalClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleEstSubmitYearRange}
                              disabled={tempEstToYear < tempEstFromYear}
                            >
                              Submit
                            </Button>
                          </Box>
                        </div>
                      </LocalizationProvider>
                    </Box>
                  </Modal>
                  <FormControl
                    sx={{
                      minWidth: 90,
                      border: "1px solid #EDF0F3",
                      borderRadius: "7px",
                      px: 1,
                      py: 0.5,
                      backgroundColor: "#fff",
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#363434",
                    }}
                    variant="outlined"
                  >
                    <Select
                      value={estYearInput}
                      onChange={(e) => {
                        setEstYearInput(Number(e.target.value));
                        setEstViewType("specificYear");
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        "& .MuiSelect-select": { py: 0.5 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      {Array.from(
                        { length: estToYear - estFromYear + 1 },
                        (_, i) => estFromYear + i
                      ).map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <div ref={dountChartRef} />
            </Paper>
          </Grid>

          {/* Quotations Section */}
          <Grid item md={4} xs={12}>
            <Paper className="graphBox">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Quotation</Typography>
                <Box display="flex" gap={2}>
                  {/* Button to open year range modal */}
                  <Button
                    onClick={() => {
                      setQuotationViewType("range"); // Switch to range mode
                      handleQuotationModalOpen();
                    }}
                    className="modalBtn"
                  >
                    Year Range
                  </Button>

                  {/* Modal for selecting year range */}
                  <Modal
                    open={quotationModalOpen}
                    onClose={handleQuotationModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        p: 4,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "16px" }}>
                            <DatePicker
                              label="From Year"
                              views={["year"]}
                              value={
                                tempQuotationFromYear
                                  ? dayjs(`${tempQuotationFromYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempQuotationFromYear(newValue.year());
                              }}
                              minDate={dayjs(`${quotationFromYear}-01-01`)}
                              maxDate={dayjs(`${quotationToYear}-12-31`)}
                              sx={{ minWidth: 120 }}
                            />
                            <DatePicker
                              label="To Year"
                              views={["year"]}
                              value={
                                tempQuotationToYear
                                  ? dayjs(`${tempQuotationToYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempQuotationToYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${
                                  tempQuotationFromYear || quotationFromYear
                                }-01-01`
                              )}
                              maxDate={dayjs(
                                `${quotationDate.getFullYear()}-12-31`
                              )}
                              sx={{ minWidth: 120 }}
                            />
                          </div>
                          <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={handleQuotationModalClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleQuotationSubmitYearRange}
                              disabled={
                                tempQuotationToYear < tempQuotationFromYear
                              }
                            >
                              Submit
                            </Button>
                          </Box>
                        </div>
                      </LocalizationProvider>
                    </Box>
                  </Modal>

                  {/* Year select for specific year mode */}
                  <FormControl
                    sx={{
                      minWidth: 90,
                      border: "1px solid #EDF0F3",
                      borderRadius: "7px",
                      px: 1,
                      py: 0.5,
                      backgroundColor: "#fff",
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#363434",
                    }}
                    variant="outlined"
                  >
                    <Select
                      value={quotationYearInput}
                      onChange={(e) => {
                        setQuotationViewType("specificYear"); // Switch to specific year mode
                        setQuotationYearInput(Number(e.target.value));
                        // Optionally clear range years to avoid confusion
                        setTempQuotationFromYear(null);
                        setTempQuotationToYear(null);
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        "& .MuiSelect-select": { py: 0.5 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      {Array.from(
                        { length: quotationToYear - quotationFromYear + 1 },
                        (_, i) => quotationFromYear + i
                      ).map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Chart Container */}
              <div ref={barChartRef} />
            </Paper>
          </Grid>

          {/* Proposals Section */}
          <Grid item md={4} xs={12}>
            <Paper className="graphBox">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Proposals</Typography>

                <Box display="flex" gap={2}>
                  <Button onClick={handlePropModalOpen} className="modalBtn">
                    Year Range
                  </Button>

                  {/* Year Range Modal */}
                  <Modal
                    open={propModalOpen}
                    onClose={handlePropModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        p: 4,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "16px" }}>
                            <DatePicker
                              label="From Year"
                              views={["year"]}
                              value={
                                tempPropFromYear
                                  ? dayjs(`${tempPropFromYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempPropFromYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempPropFromYear || propFromYear}-01-01`
                              )}
                              maxDate={dayjs(
                                `${tempPropToYear || propToYear}-12-31`
                              )}
                              sx={{ minWidth: 120 }}
                            />
                            <DatePicker
                              label="To Year"
                              views={["year"]}
                              value={
                                tempPropToYear
                                  ? dayjs(`${tempPropToYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempPropToYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${tempPropFromYear || propFromYear}-01-01`
                              )}
                              maxDate={dayjs(`${propDate.getFullYear()}-12-31`)}
                              sx={{ minWidth: 120 }}
                            />
                          </div>
                          <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={handlePropModalClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => {
                                if (tempPropToYear >= tempPropFromYear) {
                                  setPropViewType("yearRange");
                                  setPropFromYear(tempPropFromYear);
                                  setPropToYear(tempPropToYear);
                                  handlePropModalClose();
                                }
                              }}
                              disabled={tempPropToYear < tempPropFromYear}
                            >
                              Submit
                            </Button>
                          </Box>
                        </div>
                      </LocalizationProvider>
                    </Box>
                  </Modal>

                  {/* Specific Year Select Dropdown */}
                  <FormControl
                    sx={{
                      minWidth: 90,
                      border: "1px solid #EDF0F3",
                      borderRadius: "7px",
                      px: 1,
                      py: 0.5,
                      backgroundColor: "#fff",
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#363434",
                    }}
                    variant="outlined"
                  >
                    <Select
                      value={propYearInput}
                      onChange={(e) => {
                        setPropViewType("specificYear");
                        setPropYearInput(Number(e.target.value));
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        "& .MuiSelect-select": { py: 0.5 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      {Array.from(
                        { length: propToYear - propFromYear + 1 },
                        (_, i) => propFromYear + i
                      ).map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Chart Render Div */}
              <div ref={lineChartRef} />
            </Paper>
          </Grid>

          {/* Contracts Section */}
          <Grid item md={4} xs={12}>
            <Paper className="graphBox" elevation={2} sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6">Contract</Typography>
                <Box display="flex" gap={2}>
                  <Button
                    onClick={handleContractModalOpen}
                    className="modalBtn"
                  >
                    Year Range
                  </Button>
                  <Modal
                    open={contractModalOpen}
                    onClose={handleContractModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        p: 4,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "16px" }}>
                            <DatePicker
                              label="From Year"
                              views={["year"]}
                              value={
                                tempContractFromYear
                                  ? dayjs(`${tempContractFromYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempContractFromYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${
                                  tempContractFromYear || propContractFromYear
                                }-01-01`
                              )}
                              maxDate={dayjs(
                                `${
                                  tempContractToYear || propContractToYear
                                }-12-31`
                              )}
                              sx={{ minWidth: 120 }}
                            />
                            <DatePicker
                              label="To Year"
                              views={["year"]}
                              value={
                                tempContractToYear
                                  ? dayjs(`${tempContractToYear}-01-01`)
                                  : null
                              }
                              onChange={(newValue) => {
                                if (newValue)
                                  setTempContractToYear(newValue.year());
                              }}
                              minDate={dayjs(
                                `${
                                  tempContractFromYear || propContractFromYear
                                }-01-01`
                              )}
                              maxDate={dayjs(
                                `${contractDate.getFullYear()}-12-31`
                              )}
                              sx={{ minWidth: 120 }}
                            />
                          </div>
                          <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={handleContractModalClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleContractSubmitYearRange}
                              disabled={
                                tempContractToYear < tempContractFromYear
                              }
                            >
                              Submit
                            </Button>
                          </Box>
                        </div>
                      </LocalizationProvider>
                    </Box>
                  </Modal>
                  <FormControl
                    sx={{
                      minWidth: 90,
                      border: "1px solid #EDF0F3",
                      borderRadius: "7px",
                      px: 1,
                      py: 0.5,
                      backgroundColor: "#fff",
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#363434",
                    }}
                    variant="outlined"
                  >
                    <Select
                      value={propContractYearInput}
                      onChange={(e) => {
                        setPropContractYearInput(Number(e.target.value));
                        setPropContractViewType("specificYear");
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        "& .MuiSelect-select": { py: 0.5 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      {Array.from(
                        {
                          length: propContractToYear - propContractFromYear + 1,
                        },
                        (_, i) => propContractFromYear + i
                      ).map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <div ref={areaChartRef} />
            </Paper>
          </Grid>
        </Grid>
      </div>
    </StyledDashboardLayout>
  );
}

export default Dashboard;
